/**
 * @module getMagnoliaItem
 */
const axios = require('axios');
const jp = require('jsonpath');
const { getAPIBase } = require('./getAPIBase');
const {
  MAGNOLIA_ENDPOINT_WORKSPACE_MAP,
  STATUS_TYPES,
} = require('../../constants');

/**
 * @typedef {object} MgnlEnvVars
 * @property {string} REACT_APP_CMS - Default CMS base URL used if Author or Public variables not defined (e.g. 'https://author-live-lc-prod.de.magnolia-cloud.com').
 * @property {string} REACT_APP_MGNL_AUTHOR - Magnolia Author base URL (e.g. 'https://public-live-lc-prod.de.magnolia-cloud.com').
 * @property {string} REACT_APP_MGNL_BASE_AUTHOR - Magnolia Author base path (e.g. '').
 * @property {string} REACT_APP_MGNL_BASE_PUBLIC - Magnolia Public base path (e.g. '').
 * @property {string} REACT_APP_MGNL_HOST - Magnolia Public base URL (e.g. 'https://author-live-lc-prod.de.magnolia-cloud.com').
 */

/**
 * @typedef MgnlPromise
 * @property {string} caller - The path of the file from which the function was called.
 * @property {object} data - The data associated with the Magnolia item.
 * @property {Date} expires - The Date object representing the time the API call repeat logic expires.
 * @property {string} path - The path of the requested Magnolia item.
 * @property {string} status - One of the valid STATUS_TYPES values denoting the status of the promise.
 */

/**
 * Convenience object to store Magnolia API promises to avoid duplicate calls.
 * Note: The structure of each item should match the MgnlPromise typedef.
 */
const mgnlPromises = {};

// Expiry time of completed API call (15 seconds).
const mgnlCallExpiryDuration = 15 * 1000;

/**
 * Convenience function to return the workspace value for the specified path.
 *
 * @param {string} path - The path value.
 * @param {object} [workspaceMap] - Data object map of paths and workspace values. If not provided, the default values found in the MAGNOLIA_ENDPOINT_WORKSPACE_MAP constant will be used.
 *
 * @example
 * const workspaceMap = {
 *   '/.rest/delivery/footer': 'footer',
 *   '/.rest/delivery/menu': 'menu',
 * };
 * const path = '/.rest/delivery/menu';
 *
 * // Returns 'menu'.
 * const workspaceResult = getWorkspace(path, workspaceMap);
 *
 * @returns {string} The provided path workspace value.
 */
const getWorkspace = (path, workspaceMap = MAGNOLIA_ENDPOINT_WORKSPACE_MAP) => {
  let workspace = '';
  if (
    !workspaceMap ||
    typeof workspaceMap !== 'object' ||
    Array.isArray(workspaceMap) ||
    !Object.keys(workspaceMap).length
  ) {
    return workspace;
  }
  Object.keys(workspaceMap).some((endpoint) => {
    if (path.includes(endpoint)) {
      workspace = workspaceMap[endpoint];
      return true;
    }
    return false;
  });
  return workspace;
};

/**
 * Convenience function to add `@workspace` attributes to items in the specified data object.
 *
 * Note: The ignore directive is used since this method is presently only called via getMagnoliaItem, which passes in its default value, MAGNOLIA_ENDPOINT_WORKSPACE_MAP, as a fallback.
 *
 * @param {object} params - The function params object.
 * @param {object} params.data - Data object to inspect and to which to add workspace items.
 * @param {string} params.endpointUrl - The endpoint URL value for the workspace.
 * @param {object} [params.workspaceMap] - Data object map of paths and workspace values. If not provided, the default values found in the MAGNOLIA_ENDPOINT_WORKSPACE_MAP constant will be used.
 */
/* istanbul ignore next */
const addWorkspaceToItems = ({
  data,
  endpointUrl,
  workspaceMap = MAGNOLIA_ENDPOINT_WORKSPACE_MAP,
}) => {
  const pathArrays = jp.paths(data, "$..['@id']");
  const paths = pathArrays.map((p) => jp.stringify(p));
  const parents = paths.map((p) => jp.parent(data, p));
  parents.forEach(
    // eslint-disable-next-line no-return-assign
    (p) =>
      // eslint-disable-next-line no-param-reassign
      (p['@workspace'] = getWorkspace(
        endpointUrl,
        workspaceMap || MAGNOLIA_ENDPOINT_WORKSPACE_MAP,
      )),
  );
};

/**
 * Convenience function to fetch and return the Magnolia item for the specified path.
 *
 * @param {object} params - The function params object.
 * @param {string} [params.caller] - The path of the file from which this function was called.
 * @param {object} [params.fallbackdata] - The fallback data to use when necessary.
 * @param {object} [params.forceCall] - Optional boolean flag denoting whether or not to force the call and make the network request without the cached promise data returned, if found.
 * @param {object} [params.ip] - Optional IP address to send as header with request.
 * @param {MgnlEnvVars} params.mgnlEnvVars - Object of required Magnolia ENV variables. If not set, defaults and attempts to use `process.env`.
 * @param {string} params.path - The path of the Magnolia item to retrieve.
 * @param {object} [params.res] - The response object.
 * @param {object} [params.workspaceMap] - Data object map of paths and workspace values. If not provided, the default values found in the MAGNOLIA_ENDPOINT_WORKSPACE_MAP constant will be used.
 *
 * @returns {object} The Magnolia item.
 */
const getMagnoliaItem = async ({
  caller = null,
  fallbackdata = false,
  forceCall = false,
  ip,
  mgnlEnvVars,
  path,
  res,
  workspaceMap = MAGNOLIA_ENDPOINT_WORKSPACE_MAP,
}) => {
  /* istanbul ignore next */
  const envVars = mgnlEnvVars || process.env;
  try {
    const invokeTime = new Date();
    if (path) {
      // Check to see if the current path is already stored in the list of
      // promises, adding it new if it does not exist or is expired.
      const formattedUrl = getAPIBase(envVars) + path;
      /* istanbul ignore next */
      if (
        mgnlPromises &&
        mgnlPromises[path] &&
        mgnlPromises[path].data &&
        mgnlPromises[path].expires &&
        invokeTime < mgnlPromises[path].expires &&
        !forceCall
      ) {
        return mgnlPromises[path].data;
      }

      mgnlPromises[path] = {
        caller,
        data: null,
        expires: new Date(invokeTime.getTime() + mgnlCallExpiryDuration),
        path,
        status: STATUS_TYPES.PENDING,
      };
      if (
        res &&
        res.timing &&
        res.timing.requests &&
        res.timing.requests &&
        !res.timing.requests[path]
      ) {
        res.timing.requests[path] = {
          caller,
          path,
          start: new Date(),
        };
      }

      const data = await axios.get(formattedUrl, {
        headers: {
          http_x_forwarded_for: ip,
        },
      });
      const endTime = new Date();
      mgnlPromises[path].data = data.data;
      mgnlPromises[path].expires = new Date(
        endTime.getTime() + mgnlCallExpiryDuration,
      );
      mgnlPromises[path].status = STATUS_TYPES.RESOLVED;
      if (
        res &&
        res.timing &&
        res.timing.requests &&
        res.timing.requests[path]
      ) {
        res.timing.requests[path].resolve = new Date();
        const { resolve, start } = res.timing.requests[path];
        res.timing.requests[path].duration = Math.abs(resolve - start);
        res.timing.requests[path].status = STATUS_TYPES.RESOLVED;
      }

      addWorkspaceToItems({
        data: mgnlPromises[path].data,
        endpointUrl: path,
        workspaceMap,
      });
      return mgnlPromises[path].data;
    }
    return fallbackdata;
  } catch (error) {
    /* istanbul ignore next */
    if (mgnlPromises[path]) {
      const errorTime = new Date();
      mgnlPromises[path].data = null;
      mgnlPromises[path].expires = new Date(
        errorTime.getTime() + mgnlCallExpiryDuration,
      );
      mgnlPromises[path].status = STATUS_TYPES.ERROR;
      if (
        res &&
        res.timing &&
        res.timing.requests &&
        res.timing.requests[path]
      ) {
        res.timing.requests[path].resolve = new Date();
        const { resolve, start } = res.timing.requests[path];
        res.timing.requests[path].duration = Math.abs(resolve - start);
        res.timing.requests[path].status = STATUS_TYPES.ERROR;
      }
    }
    return fallbackdata;
  }
};

module.exports = {
  addWorkspaceToItems,
  getMagnoliaItem,
  getWorkspace,
};
